import React from 'react'
import { Box, Card, CardContent, Grid, Skeleton } from '@mui/material'

import { innerContentLayoutSx } from '@/layout-blueprints/chromeComponentSx'

const WAVE = 'wave'

function Bar(props) {
  return (
    <Skeleton
      variant="rectangular"
      animation={WAVE}
      sx={{ borderRadius: 1 }}
      {...props}
    />
  )
}

function TextBar({ width = '100%', height = 20, sx, ...rest  }) {
  return (
    <Skeleton
      variant="text"
      animation={WAVE}
      width={width}
      height={height}
      sx={sx}
      {...rest}
    />
  )
}

function TableVariant({ rowCount, columnCount  }) {
  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Box sx={[{
        display: "flex",
        alignItems: "center",
        py: "0.75rem",
        px: "1.25rem",
        mb: 0,
        backgroundColor: "#fff",
        padding: "0.75rem 1.25rem",
        marginBottom: 0,
        borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
        "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
        "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
      }, { pr: '0.5rem' }]}>
        <Box sx={{ width: '100%' }}>
          <TextBar width="28%" height={16} />
          <TextBar width="42%" height={28} />
        </Box>
      </Box>
      <CardContent sx={{ p: 2 }}>
        <Bar height={40} sx={{ mb: 1.5 }} />
        {Array.from({ length: rowCount  }).map((_, rowIndex) => (
          <Box
            key={`table-skel-row-${rowIndex}`}
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 1,
              alignItems: 'center',
            }}
          >
            {Array.from({ length: columnCount  }).map((_, colIndex) => (
              <Bar
                key={`table-skel-cell-${rowIndex}-${colIndex}`}
                height={36}
                sx={{ flex: 1 }}
              />
            ))}
          </Box>
        ))}
      </CardContent>
    </Card>
  )
}

function PageVariant() {
  return (
    <Box sx={{ p: 3 }} role="status" aria-label="Loading">
      <TextBar width="36%" height={36} />
      <TextBar width="52%" height={20} sx={{ mb: 3 }} />
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Bar height={180} sx={{ mb: 2 }} />
          <TextBar width="80%" />
          <TextBar width="70%" />
          <TextBar width="60%" />
        </CardContent>
      </Card>
    </Box>
  )
}

function DashboardVariant() {
  return (
    <Box
      role="status"
      aria-label="Loading"
      sx={[
        innerContentLayoutSx({ fixed: true  }),
        { display: 'flex', minHeight: '70vh' },
      ]}
    >
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          p: 2,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <TextBar width="70%" height={24} sx={{ mb: 2 }} />
        {Array.from({ length: 8  }).map((_, index) => (
          <Bar
            key={`dash-nav-${index}`}
            height={36}
            sx={{ mb: 1 }}
          />
        ))}
      </Box>
      <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
        <Bar height={72} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {Array.from({ length: 4  }).map((_, index) => (
            <Grid key={`dash-card-${index}`} size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <TextBar width="50%" height={24} />
                  <Bar height={120} sx={{ my: 1.5 }} />
                  <TextBar width="90%" />
                  <TextBar width="75%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  )
}

function SidebarVariant() {
  return (
    <Box sx={{ p: 2 }} role="status" aria-label="Loading">
      {Array.from({ length: 10  }).map((_, index) => (
        <Box
          key={`sidebar-skel-${index}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}
        >
          <Skeleton
            variant="circular"
            animation={WAVE}
            width={22}
            height={22}
          />
          <TextBar width={`${60 + (index % 3) * 10}%`} height={20} />
        </Box>
      ))}
    </Box>
  )
}

function PlanVariant() {
  return (
    <Card variant="outlined"
      sx={{
        m: 1.25,
        minWidth: 260,
        textAlign: 'center',
      }}
    >
      <CardContent>
        <TextBar width="40%" height={22} sx={{ mx: 'auto' }} />
        <TextBar width="55%" height={36} sx={{ mx: 'auto', my: 1 }} />
        <TextBar width="70%" sx={{ mx: 'auto' }} />
        <Box sx={{ mt: 2 }}>
          {Array.from({ length: 4  }).map((_, index) => (
            <TextBar
              key={`plan-line-${index}`}
              width={`${80 - index * 8}%`}
              sx={{ mx: 'auto' }}
            />
          ))}
        </Box>
        <Bar height={40} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  )
}

function FormVariant({ rowCount  }) {
  return (
    <Box sx={{ p: 2, width: '100%' }} role="status" aria-label="Loading">
      {Array.from({ length: rowCount  }).map((_, index) => (
        <Bar key={`form-skel-${index}`} height={48} sx={{ mb: 1.5 }} />
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Bar height={36} width={88} />
        <Bar height={36} width={108} />
      </Box>
    </Box>
  )
}

function SearchVariant() {
  return (
    <Grid size={12}>
      <Bar height={56} sx={{ mb: 3 }} />
    </Grid>
  )
}

function ChartVariant({ height  }) {
  return (
    <Bar
      height={height}
      sx={{ width: '100%' }}
      aria-label="Loading chart"
    />
  )
}

function InlineVariant({ width = 120, height = 24  }) {
  return <TextBar width={width} height={height} />
}

function RowVariant({ columnCount  }) {
  return (
    <tr>
      {Array.from({ length: columnCount  }).map((_, index) => (
        <td key={`row-skel-${index}`} style={{ padding: '8px 12px' }}>
          <Bar height={20} />
        </td>
      ))}
    </tr>
  )
}

function AccordionVariant() {
  return (
    <Bar height={56} sx={{ mb: 1, width: '100%' }} />
  )
}

function ButtonVariant({ width = 140, height = 40  }) {
  return (
    <Bar
      height={height}
      width={width}
      sx={{ display: 'inline-block', m: 0.5 }}
    />
  )
}

function MenuItemVariant() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
      <Skeleton variant="circular" animation={WAVE} width={18} height={18} />
      <TextBar width={140} height={20} />
    </Box>
  )
}

function TilesVariant({ count  }) {
  return (
    <Grid size={12}>
      <Card variant="outlined" sx={{ width: '100%' }}>
        <Grid container>
          {Array.from({ length: count  }).map((_, index) => (
            <Grid
              key={`tile-skel-${index}`}
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={{ p: 2 }}
            >
              <TextBar width="30%" height={32} sx={{ mx: 'auto' }} />
              <TextBar width="60%" height={22} sx={{ mx: 'auto' }} />
              <TextBar width="80%" sx={{ mx: 'auto' }} />
            </Grid>
          ))}
        </Grid>
      </Card>
    </Grid>
  )
}

function ArticleVariant() {
  return (
    <Box sx={{ p: 3 }} role="status" aria-label="Loading">
      <TextBar width="48%" height={40} />
      <TextBar width="24%" height={18} sx={{ mb: 3 }} />
      <Card variant="outlined">
        <CardContent>
          <Bar height={220} sx={{ mb: 2 }} />
          {Array.from({ length: 8  }).map((_, index) => (
            <TextBar
              key={`article-line-${index}`}
              width={`${92 - (index % 4) * 8}%`}
            />
          ))}
        </CardContent>
      </Card>
    </Box>
  )
}

function DialogActionsVariant() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        p: 1.5,
        width: '100%',
      }}
    >
      <Bar height={36} width={88} />
      <Bar height={36} width={108} />
    </Box>
  )
}

function DialogVariant() {
  return (
    <Card variant="outlined" sx={{ width: '100%', maxWidth: 420, m: 1 }}>
      <CardContent>
        <TextBar width="55%" height={28} sx={{ mb: 2 }} />
        <Bar height={48} sx={{ mb: 1.5 }} />
        <Bar height={48} sx={{ mb: 1.5 }} />
        <Bar height={48} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Bar height={36} width={88} />
          <Bar height={36} width={108} />
        </Box>
      </CardContent>
    </Card>
  )
}

function ProformaVariant() {
  return (
    <Box sx={{ p: 3, height: '100%' }} role="status" aria-label="Loading">
      <TextBar width="28%" height={36} sx={{ mb: 2 }} />
      <Bar height={640} sx={{ width: '100%' }} />
    </Box>
  )
}

/**
 * Layout-matching MUI skeletons for GraphQL / Suspense loading states.
 *
 * @param {'table'|'page'|'dashboard'|'sidebar'|'plan'|'form'|'search'|'chart'|'inline'|'row'|'accordion'|'button'|'menuItem'|'tiles'|'article'|'dialog'|'dialogActions'|'proforma'} variant
 */
export function LoadingSkeleton({
  variant = 'page',
  rowCount = 8,
  columnCount = 5,
  height = 250,
  width,
  count = 4,
}) {
  switch (variant) {
    case 'table':
      return <TableVariant rowCount={rowCount} columnCount={columnCount} />
    case 'dashboard':
      return <DashboardVariant />
    case 'sidebar':
      return <SidebarVariant />
    case 'plan':
      return <PlanVariant />
    case 'form':
      return <FormVariant rowCount={rowCount || 5} />
    case 'search':
      return <SearchVariant />
    case 'chart':
      return <ChartVariant height={height} />
    case 'inline':
      return <InlineVariant width={width} height={height === 250 ? 24 : height} />
    case 'row':
      return <RowVariant columnCount={columnCount} />
    case 'accordion':
      return <AccordionVariant />
    case 'button':
      return <ButtonVariant width={width || 140} height={height === 250 ? 40 : height} />
    case 'menuItem':
      return <MenuItemVariant />
    case 'tiles':
      return <TilesVariant count={count} />
    case 'article':
      return <ArticleVariant />
    case 'dialog':
      return <DialogVariant />
    case 'dialogActions':
      return <DialogActionsVariant />
    case 'proforma':
      return <ProformaVariant />
    case 'page':
    default:
      return <PageVariant />
  }
}

export default LoadingSkeleton
